import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment-timezone";

import { useAppDispatch } from "../../../../redux/useDispatch";
import {
  keyReturnFromDateSelected,
  keyReturnToDateSelected,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { parseBookingQuery } from "../common/url/bookingQuery";
import { useHydrationTimeZone } from "./useHydrationTimeZone";

/**
 * Custom hook to hydrate return schedule (returnDate, returnTime, returnDays) from URL query parameters.
 *
 * @remarks
 * - Runs once on mount to parse initial URL state
 * - Converts YYYY-MM-DD date and HH:mm time to moment objects
 * - Calculates end date based on returnDays parameter
 * - Dispatches Redux actions to populate return schedule state
 *
 * @example
 * ```tsx
 * function ReturnSchedulePage() {
 *   useReturnScheduleHydration();
 *   // Return schedule state now hydrated from URL
 * }
 * ```
 */
export function useReturnScheduleHydration(locationHydrated: boolean = false) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  // The location's zone (#3219); null until the URL's location is selected —
  // or, once location hydration has finished without finding it, the
  // default zone (see useHydrationTimeZone).
  const TIMEZONE = useHydrationTimeZone(location.search, locationHydrated);

  const hydratedRef = useRef(false);
  const capturedRef = useRef(false);
  const initialParamsRef = useRef<{
    returnDate: string | null;
    returnTime: string | null;
    returnDays: string | null;
  } | null>(null);

  // Capture initial URL parameters once on mount
  useEffect(() => {
    if (capturedRef.current) return;

    const query = parseBookingQuery(location.search);
    initialParamsRef.current = {
      returnDate: query.returnDate ?? null,
      returnTime: query.returnTime ?? null,
      returnDays: query.returnDays ?? null,
    };
    capturedRef.current = true;
  }, [location.search]);

  // Reset hydration when URL changes (user navigated back and changed values)
  useEffect(() => {
    return () => {
      hydratedRef.current = false;
      capturedRef.current = false;
      initialParamsRef.current = null;
    };
  }, [location.pathname]); // Reset when pathname changes

  // Hydrate from captured URL parameters.
  // A PASSIVE effect on purpose: when the zone resolves in the same commit in
  // which the page swaps its spinner for the step, the step's own mount
  // effects (children first) have already run when this one dispatches, so
  // nothing a step initialises on mount can overwrite the URL's values.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (initialParamsRef.current === null) return;
    if (TIMEZONE === null) return;

    const { returnDate, returnTime, returnDays } = initialParamsRef.current;

    if (!returnDate) {
      hydratedRef.current = true;
      return;
    }

    // Parse date from YYYY-MM-DD format
    const dateMoment = moment.tz(returnDate, "YYYY-MM-DD", TIMEZONE);
    if (!dateMoment.isValid()) {
      console.error(`[useReturnScheduleHydration] Invalid returnDate in URL: ${returnDate}`);
      hydratedRef.current = true;
      return;
    }

    let returnFromMoment = dateMoment.clone();
    let numberOfDays = 1; // Default to 1 day

    // Apply returnTime if provided
    if (returnTime) {
      const [hours, minutes] = returnTime.split(":").map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        returnFromMoment = moment.tz(
          {
            year: dateMoment.year(),
            month: dateMoment.month(),
            date: dateMoment.date(),
            hour: hours,
            minute: minutes,
            second: 0,
            millisecond: 0,
          },
          TIMEZONE
        );
      }
    }

    // Parse returnDays parameter if provided
    if (returnDays) {
      const parsedDays = parseInt(returnDays, 10);
      if (!isNaN(parsedDays) && parsedDays > 0) {
        numberOfDays = parsedDays;
      }
    }

    // Calculate end date based on number of days
    // End time is same as start time
    const returnToMoment = returnFromMoment.clone().add(numberOfDays, "days");

    // Dispatch to Redux store
    dispatch(keyReturnFromDateSelected({ checkInDate: returnFromMoment }));
    dispatch(keyReturnToDateSelected({ checkOutDate: returnToMoment }));

    hydratedRef.current = true;
  }, [dispatch, TIMEZONE]);
}
